/* eslint-disable no-console */

/**
 * Debug Logger (namespaced + wildcard)
 * ------------------------------------------------------------
 * Lightweight utility for conditional console logging and debug UI visibility.
 *
 * Usage:
 *   import { getDebugLogger, debugVisible } from "@app/core/utilities/debugLogger";
 *   const log = getDebugLogger("auth:flow");
 *   log("mounted");
 *   log("retry-started", { attempt: 2 });
 *
 * Enable in DevTools:
 *   window.enableDebug("auth:*");        // enable auth namespace
 *   window.enableDebug("*");             // enable everything
 *   window.disableDebug();               // disable all
 *
 * Production override (TTL-gated for safety):
 *   window.enableDebugProd(8);           // enable for 8 hours
 *   window.disableDebugProd();           // disable immediately
 */

type LoggerFn = ((event: string, payload?: unknown) => void) & {
    json: (event: string, payload?: unknown) => void;
};

const cache: Record<string, LoggerFn> = {};
const LS_KEY = "debug:namespaces";
const LS_KEY_ALLOW_PROD = "debug:allowProd";
const DEFAULT_PROD_TTL_HOURS = 8;

type AllowProdRecord = { value: "true"; ts: number; ttlH: number };

function getNowMs(): number {
    return Date.now();
}

function readAllowProdActive(): boolean {
    if (typeof window === "undefined") return false;
    try {
        const raw = localStorage.getItem(LS_KEY_ALLOW_PROD);
        if (!raw) return false;
        if (raw === "true") return true; // legacy compat

        const rec = JSON.parse(raw) as AllowProdRecord;
        if (rec?.value !== "true" || !rec.ts) return false;

        const ageMs = getNowMs() - rec.ts;
        const ttlMs = (rec.ttlH || DEFAULT_PROD_TTL_HOURS) * 60 * 60 * 1000;

        if (ageMs > ttlMs) {
            localStorage.removeItem(LS_KEY_ALLOW_PROD);
            return false;
        }
        return true;
    } catch {
        return false;
    }
}

function readNamespaces(): string[] {
    if (typeof window === "undefined") return [];
    try {
        const raw = localStorage.getItem(LS_KEY) ?? "";
        return raw
            .split(",")
            .map(s => s.trim())
            .filter(Boolean);
    } catch {
        return [];
    }
}

function wildcardMatch(pattern: string, value: string): boolean {
    if (pattern === "*") return true;
    if (!pattern.includes("*")) return pattern.toLowerCase() === value.toLowerCase();

    const regex = new RegExp(
        "^" +
            pattern
                .split("*")
                .map(s => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
                .join(".*") +
            "$",
        "i"
    );
    return regex.test(value);
}

function isEnabled(namespace: string, list: string[]): boolean {
    if (list.length === 0) return false;
    return list.some(pattern => wildcardMatch(pattern, namespace));
}

export function isProdBlocked(): boolean {
    if (process.env.NODE_ENV !== "production") return false;
    return !readAllowProdActive();
}

function safeStringify(payload: unknown, pretty = false): string {
    const seen = new WeakSet();
    try {
        return JSON.stringify(
            payload,
            (_k, v) => {
                if (typeof v === "object" && v !== null) {
                    if (seen.has(v)) return "[Circular]";
                    seen.add(v);
                }
                return v;
            },
            pretty ? 2 : undefined
        );
    } catch (err) {
        return `<json-error:${(err as Error)?.message || "unknown"}>`;
    }
}

export function getDebugLogger(namespace: string): LoggerFn {
    if (cache[namespace]) return cache[namespace];

    const log: LoggerFn = (event: string, payload?: unknown) => {
        if (isProdBlocked()) return;
        if (!isEnabled(namespace, readNamespaces())) return;

        const prefix = `[${namespace}]`;
        if (payload === undefined) {
            console.log(`${prefix} ${event}`);
        } else {
            console.log(`${prefix} ${event}`, payload);
        }
    };

    log.json = (event: string, payload?: unknown) => {
        if (isProdBlocked()) return;
        if (!isEnabled(namespace, readNamespaces())) return;

        const prefix = `[${namespace}]`;
        const text = payload === undefined ? "" : safeStringify(payload, true);
        console.log(`${prefix} ${event}`, text);
    };

    cache[namespace] = log;
    return log;
}

/**
 * Check if debug logging is enabled for given namespace patterns
 *
 * @example
 *   if (debugVisible("auth:*")) console.log("auth debugging enabled");
 *   if (debugVisible()) console.log("any debugging enabled");
 */
export function debugVisible(...patterns: string[]): boolean {
    if (isProdBlocked()) return false;

    const enabled = readNamespaces();
    if (enabled.length === 0) return false;
    if (enabled.includes("*")) return true;

    if (patterns.length === 0) return enabled.length > 0;

    return patterns.some(pattern => enabled.some(ns => wildcardMatch(pattern, ns) || wildcardMatch(ns, pattern)));
}

// Window helpers for DevTools
if (typeof window !== "undefined") {
    (window as any).enableDebug = (...namespaces: string[]) => {
        localStorage.setItem(LS_KEY, namespaces.join(","));
        console.log(`Debug enabled for: ${namespaces.join(", ")}`);
    };

    (window as any).disableDebug = () => {
        localStorage.removeItem(LS_KEY);
        console.log("Debug disabled");
    };

    (window as any).enableDebugProd = (hours = DEFAULT_PROD_TTL_HOURS) => {
        const rec: AllowProdRecord = { value: "true", ts: getNowMs(), ttlH: Math.max(1, hours) };
        localStorage.setItem(LS_KEY_ALLOW_PROD, JSON.stringify(rec));
        console.log(`Debug prod override enabled for ${hours} hours`);
    };

    (window as any).disableDebugProd = () => {
        localStorage.removeItem(LS_KEY_ALLOW_PROD);
        console.log("Debug prod override disabled");
    };

    (window as any).debugStatus = () => {
        const ns = readNamespaces();
        const prodActive = readAllowProdActive();
        console.log("Debug namespaces:", ns.length ? ns : "(none)");
        console.log("Prod override:", prodActive ? "ACTIVE" : "inactive");
    };
}
